import React, { useEffect, useState } from 'react';
import { Button, Progress, Typography } from 'antd';
import { CheckCircleOutlined, CloseOutlined, DownOutlined, RedoOutlined, UpOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';

import QuoteStore from '@/stores/QuoteStore';

const { Text } = Typography;

const SUCCESS_TRAY_DISMISS_MS = 30_000;

const BackgroundUploadTray = observer(() => {
    const [expanded, setExpanded] = useState(false);
    const jobs = QuoteStore.backgroundUploadJobs;
    const active = jobs.some(job => job.status === 'queued' || job.status === 'uploading');
    const failedCount = jobs.filter(job => job.status === 'failed').length;
    const finishedCleanly = jobs.length > 0 && !active && failedCount === 0;

    useEffect(() => {
        if (!active) return;

        const warnBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnBeforeUnload);
        return () => window.removeEventListener('beforeunload', warnBeforeUnload);
    }, [active]);

    useEffect(() => {
        if (!finishedCleanly) return;

        const timeout = window.setTimeout(() => QuoteStore.dismissBackgroundUploads(), SUCCESS_TRAY_DISMISS_MS);
        return () => window.clearTimeout(timeout);
    }, [finishedCleanly]);

    if (!jobs.length || QuoteStore.backgroundUploadDismissed) return null;

    const completed = jobs.filter(job => job.status === 'completed').length;
    const failed = jobs.filter(job => job.status === 'failed').length;
    const aggregateProgress = Math.round(jobs.reduce((total, job) => total + job.progress, 0) / jobs.length);
    const current = jobs.find(job => job.status === 'uploading');
    const terminal = !active;

    return (
        <div
            data-testid="background-upload-tray"
            style={{
                position: 'fixed',
                right: 24,
                bottom: 24,
                zIndex: 1100,
                width: expanded ? 360 : 300,
                borderRadius: 10,
                background: '#fff',
                border: '1px solid #d9d9d9',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
                overflow: 'hidden',
            }}
        >
            <div
                className="d-flex align-items-center justify-content-between"
                style={{ padding: '10px 12px', background: '#fafafa', borderBottom: expanded ? '1px solid #eee' : 0 }}
            >
                <button
                    type="button"
                    onClick={() => setExpanded(value => !value)}
                    style={{
                        border: 0,
                        background: 'transparent',
                        padding: 0,
                        textAlign: 'left',
                        flex: 1,
                        cursor: 'pointer',
                    }}
                >
                    <Text strong>
                        {terminal
                            ? `Added ${completed} of ${jobs.length}`
                            : `Adding parts ${completed} of ${jobs.length}`}
                    </Text>
                    {failed ? <Text type="danger"> · {failed} failed</Text> : null}
                    {current ? (
                        <Text type="secondary" ellipsis style={{ display: 'block', maxWidth: 245, fontSize: 12 }}>
                            {current.label}
                        </Text>
                    ) : null}
                </button>
                <Button
                    type="text"
                    size="small"
                    aria-label={expanded ? 'Minimize uploads' : 'Expand uploads'}
                    icon={expanded ? <DownOutlined /> : <UpOutlined />}
                    onClick={() => setExpanded(value => !value)}
                />
                {terminal ? (
                    <Button
                        type="text"
                        size="small"
                        aria-label="Dismiss uploads"
                        icon={<CloseOutlined />}
                        onClick={() => QuoteStore.dismissBackgroundUploads()}
                    />
                ) : null}
            </div>

            <div style={{ padding: '0 12px 10px' }}>
                <Progress
                    percent={aggregateProgress}
                    showInfo={false}
                    size="small"
                    status={failed ? 'exception' : 'active'}
                />
            </div>

            {expanded ? (
                <div style={{ maxHeight: 220, overflowY: 'auto', padding: '0 12px 10px' }}>
                    {jobs.map(job => (
                        <div
                            key={job.uid}
                            className="d-flex align-items-center justify-content-between"
                            style={{ minHeight: 34, gap: 8, borderTop: '1px solid #f0f0f0' }}
                        >
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Text ellipsis style={{ display: 'block', fontSize: 12 }} title={job.label}>
                                    {job.label}
                                </Text>
                                {job.status === 'failed' && job.error ? (
                                    <Text
                                        type="danger"
                                        style={{ display: 'block', fontSize: 11 }}
                                        data-testid="upload-job-error"
                                    >
                                        {job.error}
                                    </Text>
                                ) : null}
                            </div>
                            {job.status === 'completed' ? (
                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            ) : (
                                <Text type={job.status === 'failed' ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                                    {job.status === 'uploading' ? `${job.progress}%` : job.status}
                                </Text>
                            )}
                        </div>
                    ))}
                    {failed ? (
                        <Button
                            block
                            size="small"
                            icon={<RedoOutlined />}
                            onClick={() => QuoteStore.retryFailedBackgroundUploads()}
                            style={{ marginTop: 8 }}
                        >
                            Retry failed uploads
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
});

export default BackgroundUploadTray;
